import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faBarcode, faCaretDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import Popper from 'popper.js';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, take, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { BarcodeScannerInstanceToken, BarcodeScannerInterface } from '../../interfaces/barcode-scanner.interface';
import { ODataModel, ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { DropdownModel } from '../../models/complex/dropdown.model';
import { AppNotificationType } from '../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../services/app-notification/app-notification.service';

const dropdownDebounceTime = 250; // the debounce time for the dropdown

@Component({
    selector: 'pace-core-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    standalone: false,
})
export class DropdownComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public model: DropdownModel<ODataModel>;

    @Input()
    public isReadonlyInputControl: boolean;

    @Output()
    public modelChange = new EventEmitter<DropdownModel<ODataModel>>();

    @Input()
    public parentForm: FormArray | undefined;

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<DisplayDecorator<ODataModel>>>();

    @ViewChild('dropDownButton')
    public dropDownButton: ElementRef;

    @ViewChild('dropDownMenu')
    public dropDownMenu: ElementRef;

    public popperInstance: Popper | undefined;

    private destroySubject = new Subject<void>();

    public dropdownForm: FormGroup;
    public searchInput: AbstractControl;

    public searchMatchingEntries: Array<DisplayDecorator<ODataModel>>;

    public dropdownTextInputControl: FormControl;

    public strings: {
        placeholder: string;
        searchPlaceholder: string;
    };

    public faBarcode = faBarcode;
    public faCaretDown = faCaretDown;
    public faTimes = faTimes;

    // holds a timestamp used to debounce the dropdown toggle event
    private dropdownToggleTime = 0;

    // controls if the dropdown menu is visible
    public showDropdownMenu = false;

    private readonly barcodeScanner = inject<BarcodeScannerInterface>(BarcodeScannerInstanceToken);

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetector: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.strings = {
            placeholder: <string>this.translate.instant('DROPDOWN_PLACEHOLDER'),
            searchPlaceholder: <string>this.translate.instant('DROPDOWN_SEARCH_PLACEHOLDER'),
        };

        const dropdownRequiredValidator = (control: AbstractControl): { [p: string]: unknown } | null =>
            this.checkValidity(control);
        this.searchInput = this.formBuilder.control('');
        this.dropdownForm = this.formBuilder.group(
            {
                searchInput: this.searchInput,
            },
            {
                validators: dropdownRequiredValidator,
            },
        );
        if (this.model.options.displayText) {
            this.dropdownTextInputControl = this.formBuilder.control('', dropdownRequiredValidator);
            this.dropdownForm.addControl('dropdownTextInputControl', this.dropdownTextInputControl);

            this.dropdownTextInputControl.valueChanges
                .pipe(
                    debounceTime(ConfigConstants.debounceTimeMs),
                    filter((newVal: string) => this.model.textFieldValue !== newVal),
                    tap((newVal: string) => {
                        this.model.textFieldValue = newVal;
                        this.dropdownTextInputControl.setValue(newVal, { emitEvent: false });
                        this.searchMatchingEntries = this.getSearchMatches(newVal);
                        this.changeDetector.detectChanges();
                    }),
                    takeUntil(this.destroySubject),
                )
                .subscribe();

            this.dropdownTextInputControl.setValue(this.model.textFieldValue, { emitEvent: false });
        }

        this.model.selectionChanged
            .pipe(
                tap(() => this.dropdownForm.updateValueAndValidity()),
                takeUntil(this.destroySubject),
                filter(() => this.model.options.maxSelections === 1),
                tap(() => this.onSelected(this.model.selectedEntry)),
                // Handles dynamic value updates for single-select dropdowns.
                // Not needed for multi-select dropdowns since values are rendered via <li>.
            )
            .subscribe();

        this.searchMatchingEntries = this.model.value;
        this.searchInput.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.searchMatchingEntries = this.getSearchMatches(newVal);
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.dropdownForm);
                })
                .catch((error: Error) =>
                    this.log.error(`DropdownComponent: ngOnInit: parentForm.push: ${JSON.stringify(error)}`),
                );
        }

        if (!this.model.options.displayText && this.model.options.autoSelect && this.model.value.length === 1) {
            const oldEntry = this.model.selectedEntry ? this.model.selectedEntry : undefined;
            this.model.selectByIndex(0);
            if (oldEntry !== this.model.selectedEntry) {
                this.modelChange.emit(this.model);
                this.valueChange.emit({ oldValue: oldEntry, newValue: this.model.selectedEntry });
            }
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.model && !changes.model.firstChange) {
            this.dropdownForm.updateValueAndValidity();
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.dropdownForm);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public toggleDropdown(): boolean {
        // debounce event
        const now = Date.now();
        if (now - this.dropdownToggleTime < dropdownDebounceTime) {
            return false;
        }

        // toggle dropdown
        this.dropdownToggleTime = now;
        this.showDropdownMenu = !this.showDropdownMenu;

        if (!this.showDropdownMenu) {
            this.dropdownForm.controls.searchInput.setValue('');
        }
        if (this.showDropdownMenu && this.dropDownButton && this.dropDownMenu) {
            this.popperInstance = new Popper(
                this.dropDownButton.nativeElement as HTMLElement,
                this.dropDownMenu.nativeElement as HTMLElement,
                {
                    placement: 'bottom-start',
                },
            );
            this.popperInstance.scheduleUpdate();
        } else if (typeof this.popperInstance !== 'undefined') {
            this.popperInstance.destroy();
            this.popperInstance = undefined;
        }

        return true;
    }

    public toggleDropdownFromMenu(event: UIEvent): boolean {
        // in case the model value has changed, we also need to update the searchMatchEntries, to ensure, that in the view,
        // only the relevant values are being displayed.
        this.searchMatchingEntries = this.model.value;
        event.preventDefault();

        if (typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
        }

        // check if dropdown is disabled
        if (this.dropdownDisabled()) {
            return true;
        }

        return this.toggleDropdown();
    }

    public dropdownDisabled(): boolean {
        let isDisabled = false;
        if (this.model.options) {
            isDisabled = isDisabled || this.model.options.disabled;
            isDisabled =
                isDisabled ||
                (this.model.options.maxSelections > 1 &&
                    this.model.selectedEntries.length === this.model.options.maxSelections);
        }

        return isDisabled;
    }

    public isSelected(entry: DisplayDecorator<ODataModel>): boolean {
        const selectedEntry = this.model.selectedEntries.find((e) => entry === e);

        return typeof selectedEntry !== 'undefined';
    }

    public onSelected(entry: DisplayDecorator<ODataModel>): void {
        const oldEntry = this.model.selectedEntry ? this.model.selectedEntry : undefined;
        const selectionChanged = this.model.selectByPredicate(
            (selectedOption) => selectedOption.value === entry?.value,
        );
        if (this.model.options.displayText) {
            this.model.textFieldValue = (this.model.selectedEntry?.model?.value ?? '') as string;
            this.dropdownTextInputControl.setValue(this.model.textFieldValue);
            this.showDropdownMenu = false;
            return;
        }
        this.showDropdownMenu = false;
        this.dropdownForm.controls.searchInput.setValue('');

        if (selectionChanged) {
            this.modelChange.emit(this.model);
            this.valueChange.emit({ oldValue: oldEntry, newValue: entry });
        }
        this.dropdownForm.updateValueAndValidity();
    }

    private checkValidity(control: AbstractControl): { [key: string]: unknown } | null {
        let validRequired = true;
        if (
            this.model.options.displayText &&
            this.dropdownForm &&
            this.dropdownForm.controls.dropdownTextInputControl
        ) {
            const controlValue = this.dropdownForm.controls.dropdownTextInputControl.value as string;
            if (this.model.options.required && (!controlValue || controlValue.length === 0)) {
                validRequired = false;
            }
            if (controlValue && controlValue.toString().length > this.model.options.maxLength) {
                validRequired = false;
            }
            if (controlValue && controlValue.toString().length < this.model.options.minLength) {
                validRequired = false;
            }
            return validRequired ? null : { numSelected: { valid: false, value: control.value } };
        }
        if (this.model.options.required && this.model.selectedEntries.length === 0) {
            validRequired = false;
        }
        if (this.model.options.invalidSelection) {
            return { empty: { valid: false, value: control.value } };
        }

        return validRequired ? null : { numSelected: { valid: false, value: control.value } };
    }

    private getSearchMatches(searchString: string): Array<DisplayDecorator<ODataModel>> {
        return this.model.value.filter(
            (entry) => entry.displayName.toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) !== -1,
        );
    }

    public doScan(): void {
        if (this.dropdownDisabled()) {
            return;
        }

        this.barcodeScanner
            .scan()
            .pipe(
                take(1),
                tap((scanResult) => {
                    const matchResult = this.model.value.find(
                        (option) => option.displayName.indexOf(scanResult.text) > -1,
                    );
                    if (this.model.options.displayText) {
                        this.dropdownTextInputControl.setValue(scanResult.text);
                        this.dropdownForm.updateValueAndValidity();
                    } else if (matchResult) {
                        const oldValue = this.model.selectedEntry;
                        const selectionChanged = this.model.selectByDisplayName(matchResult.displayName);
                        if (selectionChanged) {
                            this.modelChange.emit(this.model);
                            this.valueChange.emit({ oldValue: oldValue, newValue: this.model.selectedEntry.value });
                        }
                    } else {
                        this.log.warn(
                            `DropdownComponent: doScan: Scan result '${scanResult.text}' does no match any entry in dropdown.`,
                        );
                        throw scanResult;
                    }
                }),
                catchError((error) => {
                    this.log.error(`DropdownComponent: doScan: Error: ${JSON.stringify(error)}`);

                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('DROPDOWN_TEXT_SCAN_NO_MATCH_FOUND_TITLE'),
                        <string>(
                            this.translate.instant('DROPDOWN_TEXT_SCAN_ERROR_NO_BODY', { error: JSON.stringify(error) })
                        ),
                    );

                    return of(undefined);
                }),
            )
            .subscribe();
    }

    // this method is used to deselect the model for single-select dropdowns when the value is cleared.
    // Multi-select handling is already implemented in dropdown.model.ts.
    public clearDropdownSelection(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.value === '' && this.model.options.maxSelections === 1) {
            this.model.deselect();
        }
    }
}
